import { AgencyData, AgentData } from "../../models"
import { toTitleCase } from "../textcase"
import { AgencyAgentExportRow } from "./agencyExportTypes"

const agentStatusLabel = (flagActive: boolean): string => (flagActive ? "Active" : "Inactive")

const compareText = (left: string, right: string): number =>
  left.localeCompare(right, undefined, { sensitivity: "base" })

/**
 * Flatten filtered agencies into one export row per agent (office + agent level).
 * Agencies with no agents produce no rows.
 */
export const buildAgencyAgentExportRows = (
  agencies: AgencyData[],
  agents: AgentData[],
): AgencyAgentExportRow[] => {
  if (agencies.length === 0) return []

  const agencyOrder = new Map(agencies.map((agency, index) => [agency.agency_id, index]))
  const agencyIds = new Set(agencies.map((agency) => agency.agency_id))
  const agencyNameById = new Map(
    agencies.map((agency) => [agency.agency_id, toTitleCase(agency.agency_name ?? "")]),
  )

  const scopedAgents = agents.filter((agentRow) => agencyIds.has(agentRow.agency_id))

  scopedAgents.sort((left, right) => {
    const agencyIndexDiff =
      (agencyOrder.get(left.agency_id) ?? 0) - (agencyOrder.get(right.agency_id) ?? 0)
    if (agencyIndexDiff !== 0) return agencyIndexDiff

    const officeDiff = compareText(left.office_name ?? "", right.office_name ?? "")
    if (officeDiff !== 0) return officeDiff

    return compareText(left.agent_name ?? "", right.agent_name ?? "")
  })

  return scopedAgents.map((agentRow) => ({
    agency: agencyNameById.get(agentRow.agency_id) ?? toTitleCase(agentRow.agency_name ?? ""),
    office: toTitleCase(agentRow.office_name ?? ""),
    agent: toTitleCase(agentRow.agent_name ?? ""),
    phone: agentRow.contact_number ?? "",
    email: agentRow.email ?? "",
    status: agentStatusLabel(Boolean(agentRow.flag_active)),
  }))
}
